import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Twitter, Facebook, Linkedin, Link as LinkIcon, TrendingUp, Share2, Eye, Heart } from "lucide-react";
import { format } from "date-fns";

interface ShareStats {
  id: string;
  title: string;
  slug: string;
  total_shares: number;
  twitter_shares: number;
  facebook_shares: number;
  linkedin_shares: number;
  copy_shares: number;
  last_shared_at: string | null;
}

interface ViewStats {
  id: string;
  title: string;
  slug: string;
  total_views: number;
  last_viewed_at: string | null;
}

interface CombinedStats {
  id: string;
  title: string;
  slug: string;
  total_shares: number;
  total_views: number;
  total_likes: number;
  twitter_shares: number;
  facebook_shares: number;
  linkedin_shares: number;
  copy_shares: number;
  last_shared_at: string | null;
  last_viewed_at: string | null;
  last_liked_at: string | null;
}

const ShareAnalytics = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<CombinedStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalShares, setTotalShares] = useState(0);
  const [totalViews, setTotalViews] = useState(0);
  const [totalLikes, setTotalLikes] = useState(0);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/admin/login");
        return;
      }

      // Check if user has admin role
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);

      const isAdmin = roles?.some((r) => r.role === "admin");
      if (!isAdmin) {
        navigate("/admin/login");
        return;
      }

      fetchStats();
    };

    checkAuth();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      // Fetch from the new engagement stats view
      const { data: engagementStats, error } = await supabase
        .from("article_engagement_stats")
        .select("*");

      if (error) throw error;

      const combined: CombinedStats[] = (engagementStats || []).map(stat => ({
        id: stat.id,
        title: stat.title,
        slug: stat.slug,
        total_shares: stat.total_shares || 0,
        total_views: stat.total_views || 0,
        total_likes: stat.total_likes || 0,
        twitter_shares: 0, // These will be 0 since the view doesn't break down by platform
        facebook_shares: 0,
        linkedin_shares: 0,
        copy_shares: 0,
        last_shared_at: stat.last_shared_at,
        last_viewed_at: stat.last_viewed_at,
        last_liked_at: stat.last_liked_at,
      }));

      // Sort by total engagement (views + shares + likes)
      combined.sort((a, b) => 
        (b.total_views + b.total_shares + b.total_likes) - (a.total_views + a.total_shares + a.total_likes)
      );

      setStats(combined);
      const totalSharesSum = combined.reduce((sum, stat) => sum + (stat.total_shares || 0), 0);
      const totalViewsSum = combined.reduce((sum, stat) => sum + (stat.total_views || 0), 0);
      const totalLikesSum = combined.reduce((sum, stat) => sum + (stat.total_likes || 0), 0);
      setTotalShares(totalSharesSum);
      setTotalViews(totalViewsSum);
      setTotalLikes(totalLikesSum);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "twitter":
        return <Twitter className="h-4 w-4" />;
      case "facebook":
        return <Facebook className="h-4 w-4" />;
      case "linkedin":
        return <Linkedin className="h-4 w-4" />;
      case "copy":
        return <LinkIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Analytics</h1>
          <p className="text-muted-foreground">
            Track article views and shares
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Views</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalViews}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Shares</CardTitle>
              <Share2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalShares}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Twitter</CardTitle>
              <Twitter className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.reduce((sum, s) => sum + (s.twitter_shares || 0), 0)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Facebook</CardTitle>
              <Facebook className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.reduce((sum, s) => sum + (s.facebook_shares || 0), 0)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">LinkedIn</CardTitle>
              <Linkedin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.reduce((sum, s) => sum + (s.linkedin_shares || 0), 0)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Stats Table */}
        <Card>
          <CardHeader>
            <CardTitle>Article Performance</CardTitle>
            <CardDescription>
              Breakdown of shares by article and platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Article Title</TableHead>
                  <TableHead className="text-center">
                    <Eye className="inline h-4 w-4 mr-1" />
                    Views
                  </TableHead>
                  <TableHead className="text-center">
                    <Share2 className="inline h-4 w-4 mr-1" />
                    Shares
                  </TableHead>
                  <TableHead className="text-center">
                    <Twitter className="inline h-4 w-4" />
                  </TableHead>
                  <TableHead className="text-center">
                    <Facebook className="inline h-4 w-4" />
                  </TableHead>
                  <TableHead className="text-center">
                    <Linkedin className="inline h-4 w-4" />
                  </TableHead>
                  <TableHead className="text-center">
                    <LinkIcon className="inline h-4 w-4" />
                  </TableHead>
                  <TableHead>Last Activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No data yet
                    </TableCell>
                  </TableRow>
                ) : (
                  stats.map((stat) => (
                    <TableRow key={stat.id}>
                      <TableCell className="font-medium max-w-md">
                        <div className="truncate">{stat.title}</div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{stat.total_views || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{stat.total_shares || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">{stat.twitter_shares || 0}</TableCell>
                      <TableCell className="text-center">{stat.facebook_shares || 0}</TableCell>
                      <TableCell className="text-center">{stat.linkedin_shares || 0}</TableCell>
                      <TableCell className="text-center">{stat.copy_shares || 0}</TableCell>
                      <TableCell>
                        {stat.last_viewed_at || stat.last_shared_at
                          ? format(
                              new Date(stat.last_viewed_at || stat.last_shared_at || new Date()),
                              "MMM d, yyyy"
                            )
                          : "Never"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ShareAnalytics;
